import styled from '@emotion/styled'
import { AutoStack, FlexSpinner, onFormatDate } from 'components'
import {
  Status1Icon,
  Status2Icon,
  Status3Icon,
} from 'components/CustomIcons/StatusIcons'
import { useDeleteBlobMutation } from 'components/DocumentGenerator/documentGenerator.generated'
import { DownloadLink } from 'components/DownloadLink'
import { EntityList, renderEnum, renderPersona } from 'components/EntityList'
import { ConfirmDeleteDialog } from 'dialogs/ConfirmDeleteDialog'
import { FormikActions } from 'formik'
import { ProvingDetailsParticipantDocumentForm } from 'forms/ProvingDetailsForm/documents/ProvingDetailsParticipantDocumentForm'
import { Box } from 'grid'
import { isInProvingRole, omitDeep } from 'helpers'
import { useDialogState } from 'hooks/useDialogState'
import { useFormikPanelState } from 'hooks/useFormikPanelState'
import { Participant, ProvingUserRole } from 'model'
import {
  ConstrainMode,
  Icon,
  IconButton,
  PanelType,
  PersonaSize,
  SelectionMode,
  Stack,
  TooltipHost,
} from 'office-ui-fabric-react'
import { FormikPanel } from 'panels'
import * as React from 'react'
import { useTranslation } from 'react-i18next'
import { useTheme } from 'theme'
import { createFieldColumn } from 'views/helpers'
import {
  useDeleteProvingDocumentsMutation,
  useLoadProvingDocumentsQuery,
  useUpdateProvingDocumentsMutation,
} from 'views/ProvingDetailsView/hooks'
import { ParticipantListProps } from './index'

const StyledBox = styled(Box)`
  padding-top: 3px;
`

export const HotelApprovalList: React.FunctionComponent<
  ParticipantListProps
> = ({ items, provingGround, proving, params, participants, ...props }) => {
  const { t } = useTranslation()
  const theme = useTheme()
  const [panelState, showPanel, hidePanel] = useFormikPanelState()
  const [dialogState, showDialog, hideDialog] = useDialogState()
  const fieldColumn = React.useMemo(() => createFieldColumn(t), [t])
  const { loading, error, refetch } = useLoadProvingDocumentsQuery({
    variables: { id: proving!.id },
  })
  const [documentId, setDocumentId] = React.useState()
  const [documentUri, setDocumentUri] = React.useState()
  const [updateDocuments] = useUpdateProvingDocumentsMutation({
    onCompleted: (d) => {
      refetch()
    },
  })
  const [deleteDocuments] = useDeleteProvingDocumentsMutation({
    onCompleted: (d) => {
      refetch()
    },
  })
  const [deleteBlob] = useDeleteBlobMutation()
  const isProvingManager = isInProvingRole(
    proving!,
    ProvingUserRole.ProvingManager
  )
  const handleSubmit = async (values: any, actions: FormikActions<any>) => {
    actions.setSubmitting(true)
    try {
      let block: string

      switch (values.type) {
        case 'bookingConfirmationRouteHotel':
          block = 'fix5'
          break
        case 'bookingConfirmationFerry':
          block = 'fix4'
          break
        case 'bookingConfirmationHotel':
          block = 'fix1'
          break
        case 'bookingConfirmationRentalCar':
          block = 'fix2'
          break
        case 'bookingConfirmationFlight':
          block = 'fix3'
          break
        default:
          block = values.block ? values.block : 'default'
      }

      const newValues = omitDeep(values, ['name'])

      await updateDocuments({
        variables: {
          provingId: proving!._id,
          input: {
            block,
            ...newValues,
          },
        },
      })
      actions.setStatus(null)
    } catch (err) {
      actions.setStatus({ error: err })
    } finally {
      actions.setSubmitting(false)
    }
    hidePanel()
  }

  const columns = [
    fieldColumn('person', {
      onRender: (item: Participant, i, col) => {
        if (
          (!item.person &&
            item.unknownPerson &&
            item.unknownPerson.isUnknown === true &&
            item.unknownPerson.personType === 'user') ||
          (!item.person &&
            item.unknownPerson &&
            item.unknownPerson.isUnknown === true &&
            item.unknownPerson.personType === 'external')
        ) {
          return t('model.proving.participant.participantUnknown')
        } else if (
          (!item.person &&
            item.unknownPerson &&
            item.unknownPerson.isUnknown === false &&
            item.unknownPerson.personType === 'user') ||
          (!item.person &&
            item.unknownPerson &&
            item.unknownPerson.isUnknown === false &&
            item.unknownPerson.personType === 'external')
        ) {
          return `${item.unknownPerson.surName}, ${item.unknownPerson.firstName}`
        } else if (
          item.unknownPerson &&
          item.unknownPerson.isUnknown &&
          item.unknownPerson.personType === 'worker'
        ) {
          return item.percentage + '%, ' + item.unknownPerson.department
        } else if (
          item.unknownPerson &&
          item.unknownPerson.isUnknown &&
          item.unknownPerson.personType === 'supplier'
        ) {
          return item.unknownPerson.supplier
            ? `${item.unknownPerson.surName}, ${item.unknownPerson.firstName} - ${item.unknownPerson.supplier}`
            : '[Teilnehmer noch unbekannt]'
        } else if (
          item.unknownPerson &&
          !item.unknownPerson.isUnknown &&
          item.unknownPerson.personType === 'worker'
        ) {
          return (
            <Stack horizontal={true}>
              <Stack.Item>
                {renderPersona({
                  size: PersonaSize.size24,
                })(item, i, col)}
              </Stack.Item>
              <Stack.Item>
                <StyledBox>{item.percentage}% </StyledBox>
              </Stack.Item>
            </Stack>
          )
        } else if (item.headOfProving && item.headOfProving.isHeadOfProving) {
          return (
            <Stack horizontal={true}>
              <Stack.Item>
                {renderPersona({
                  size: PersonaSize.size24,
                })(item, i, col)}
              </Stack.Item>
              <TooltipHost
                content={
                  t('model.proving.participant.headOfProving') +
                  `: ${onFormatDate(
                    new Date(item.headOfProving.dateFrom)
                  )} - ${onFormatDate(new Date(item.headOfProving.dateTo))}`
                }
              >
                <Stack.Item>
                  <StyledBox>
                    <Icon iconName="PartyLeader" />
                  </StyledBox>
                </Stack.Item>
              </TooltipHost>
            </Stack>
          )
        } else {
          return renderPersona({
            size: PersonaSize.size24,
          })(item, i, col)
        }
      },
      minWidth: 155,
      ungroupable: true,
    }),
    fieldColumn('department', {
      onRender: (item: Participant, i, col) => {
        return item.person && item.person.department
          ? item.person.department
          : item.unknownPerson && item.unknownPerson.department
          ? item.unknownPerson.department
          : ''
      },
      ungroupable: true,
    }),
    fieldColumn('orderId'),
    fieldColumn('accomodation.accomodationType', {
      onRender: (item: Participant, i, col) => {
        if (item.accomodation && item.accomodation.bookingStatus) {
          switch (item.accomodation.bookingStatus) {
            case 'open':
              return (
                <Box>
                  <Status1Icon />{' '}
                  {item &&
                  item.accomodation &&
                  item.accomodation.accomodation &&
                  item.accomodation.accomodation.title
                    ? `${item.accomodation.accomodation.title}, ${item.accomodation.accomodation.address}`
                    : renderEnum(t, 'accomodationType')(item, i, col)}
                </Box>
              )
            case 'workInProgress':
              return (
                <Box>
                  <Status2Icon />{' '}
                  {item &&
                  item.accomodation &&
                  item.accomodation.accomodation &&
                  item.accomodation.accomodation.title
                    ? `${item.accomodation.accomodation.title}, ${item.accomodation.accomodation.address}`
                    : renderEnum(t, 'accomodationType')(item, i, col)}
                </Box>
              )
            case 'finished':
              return (
                <Box>
                  <Status3Icon />{' '}
                  {item &&
                  item.accomodation &&
                  item.accomodation.accomodation &&
                  item.accomodation.accomodation.title
                    ? `${item.accomodation.accomodation.title}, ${item.accomodation.accomodation.address}`
                    : renderEnum(t, 'accomodationType')(item, i, col)}
                </Box>
              )
            default:
              return item &&
                item.accomodation &&
                item.accomodation.accomodation &&
                item.accomodation.accomodation.title
                ? item.accomodation.accomodation.title
                : renderEnum(t, 'accomodationType')(item, i, col)
          }
        }
      },
      getGroupValue: (item: Participant) =>
        t(
          `enum.accomodationType.${
            item.accomodation && item.accomodation.accomodationType
          }`
        ),
      ungroupable: true,
      minWidth: 150,
    }),
    fieldColumn('accomodationDateFrom', {
      onRender: (item: Participant, i, col) => {
        if (item.accomodation && item.accomodation.dateFrom) {
          return onFormatDate(new Date(item.accomodation.dateFrom))
        } else {
          return ''
        }
      },
    }),
    fieldColumn('accomodationDateTo', {
      onRender: (item: Participant, i, col) => {
        if (item.accomodation && item.accomodation.dateTo) {
          return onFormatDate(new Date(item.accomodation.dateTo))
        } else {
          return ''
        }
      },
    }),
    fieldColumn('documents', {
      onRender: (item: Participant, i, col) => {
        const documents: any[] = []
        // tslint:disable-next-line: no-unused-expression
        proving &&
          proving.documents &&
          proving.documents.forEach((d) => {
            if (
              d.participantId === item._id &&
              d.type === 'bookingConfirmationHotel'
            ) {
              documents.push(
                <div>
                  <IconButton
                    iconProps={{ iconName: 'Edit' }}
                    styles={{ root: { color: theme.palette.red, height: 20 } }}
                    disabled={!!isProvingManager || proving.status === 8}
                    onClick={() =>
                      showPanel({
                        documentId: d._id,
                        participantId: d.participantId,
                        type: d.type,
                        name: d.name,
                      })
                    }
                  />
                  <IconButton
                    iconProps={{ iconName: 'Delete' }}
                    styles={{ root: { color: theme.palette.red, height: 20 } }}
                    disabled={!!isProvingManager || proving.status === 8}
                    onClick={() => {
                      setDocumentId(d._id)
                      setDocumentUri(d.uri)
                      showDialog()
                    }}
                  />
                  <DownloadLink
                    uri={d.uri}
                    styles={{
                      root: {
                        alignSelf: 'start',
                      },
                    }}
                  >
                    {d.name}
                  </DownloadLink>
                </div>
              )
            }
          })
        return <AutoStack>{documents}</AutoStack>
      },
    }),
  ]

  return (
    <FlexSpinner loading={loading} error={error}>
      <EntityList
        alternate={true}
        stickyHeader={true}
        selectionMode={SelectionMode.single}
        items={items}
        constrainMode={ConstrainMode.horizontalConstrained}
        columns={columns}
        {...props}
      />
      <FormikPanel
        {...panelState}
        type={PanelType.large}
        onSubmit={handleSubmit}
      >
        <ProvingDetailsParticipantDocumentForm
          proving={proving!}
          params={params}
          participants={participants}
        />
      </FormikPanel>
      <ConfirmDeleteDialog
        {...dialogState}
        itemCount={1}
        onConfirm={async () => {
          await deleteDocuments({
            variables: {
              provingId: proving!._id,
              documentIds: [documentId],
            },
          })
          await deleteBlob({
            variables: {
              uri: documentUri,
            },
          })
          hideDialog()
          refetch()
        }}
      />
    </FlexSpinner>
  )
}
